import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement);
/* Filler and Tooltip stay local to this chart so the other charts of the app keep their own look */
const CHART_PLUGINS = [Filler, Tooltip];

var api = require("../../../utils/api.js");

const REPORT_PERIOD_MS = 30000;                 // every bus reports online every 30 s
const OFFLINE_GAP_MS = REPORT_PERIOD_MS * 2;    // two missed reports means the bus is offline
const BUCKET_MS = OFFLINE_GAP_MS;               // one chart point per offline window
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_MS = DAY_MS;               // the chart opens on the last 24 hours
const MAX_RANGE_MS = 7 * DAY_MS;                // a wider range means too many events to request
const EVENTS_REFRESH_MS = 30000;
const LINE_COLOR = "#6366f1";
const TEMP_COLOR = "#f97316";
const ACCEL_COLOR = "#10b981";
const TEMPERATURE_EVENT = "TEMPERATURE";
const ACCELEROMETER_EVENT = "VALUE";

function floorToBucket(timestamp) {
  return Math.floor(timestamp / BUCKET_MS) * BUCKET_MS;
}

function pad(value) {
  return String(value).padStart(2, "0");
}

function fmtTime(timestamp) {
  const date = new Date(timestamp);
  return pad(date.getHours()) + ":" + pad(date.getMinutes());
}

function fmtDate(timestamp) {
  const date = new Date(timestamp);
  return pad(date.getDate()) + "/" + pad(date.getMonth() + 1);
}

function fmtDateTime(timestamp) {
  return fmtDate(timestamp) + "/" + new Date(timestamp).getFullYear() + " " + fmtTime(timestamp);
}

/* Ranges longer than a day need the day on the axis, otherwise the hour is enough */
function fmtLabel(timestamp, withDay) {
  if (!withDay) return fmtTime(timestamp);
  return fmtDate(timestamp) + " " + fmtTime(timestamp);
}

/* datetime-local works with local time strings: YYYY-MM-DDTHH:mm */
function toInputValue(timestamp) {
  const date = new Date(timestamp);
  return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) +
    "T" + pad(date.getHours()) + ":" + pad(date.getMinutes());
}

function fromInputValue(value) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return isNaN(timestamp) ? null : timestamp;
}

/* One point per bucket: 1 while the bus keeps reporting, 0 once it goes silent for a
   whole bucket, null before its first report of the window (no data to show yet) */
function buildSeries(report) {
  var labels = [];
  var times = [];
  var points = [];
  var firstReport = null;
  var lastReport = null;

  if (report.from == null) return { labels, times, points, firstReport, lastReport };

  const buckets = Math.max(1, Math.round((report.to - report.from) / BUCKET_MS));
  const withDay = (report.to - report.from) > DAY_MS;
  points = new Array(buckets).fill(null);
  for (let i = 0; i < buckets; i++) {
    const bucketStart = report.from + i * BUCKET_MS;
    labels.push(fmtLabel(bucketStart, withDay));
    /* the axis label is short, the tooltip shows the whole date and time of the bucket */
    times.push(bucketStart);
  }

  var firstIndex = -1;
  for (let i = 0; i < report.events.length; i++) {
    const timestamp = report.events[i].t;
    const index = Math.floor((timestamp - report.from) / BUCKET_MS);
    if (index < 0 || index >= buckets) continue;
    points[index] = 1;
    if (firstIndex === -1 || index < firstIndex) firstIndex = index;
    if (firstReport === null || timestamp < firstReport) firstReport = timestamp;
    if (lastReport === null || timestamp > lastReport) lastReport = timestamp;
  }

  for (let i = firstIndex; i > -1 && i < buckets; i++) {
    if (points[i] === null) points[i] = 0;
  }

  return { labels, times, points, firstReport, lastReport };
}

/* Average reading per bucket on the same axis as the connectivity chart,
   null where the sensor sent nothing so the gaps stay visible */
function buildReadingSeries(report, readings) {
  var points = [];
  var lastReading = null;
  var lastReadingTime = null;

  if (report.from == null) return { points, lastReading, lastReadingTime };

  const buckets = Math.max(1, Math.round((report.to - report.from) / BUCKET_MS));
  var sums = new Array(buckets).fill(0);
  var counts = new Array(buckets).fill(0);

  for (let i = 0; i < readings.length; i++) {
    const reading = readings[i];
    const value = Number(reading.v);
    if (isNaN(value)) continue;
    const index = Math.floor((reading.t - report.from) / BUCKET_MS);
    if (index < 0 || index >= buckets) continue;
    sums[index] += value;
    counts[index]++;
    if (lastReadingTime === null || reading.t > lastReadingTime) {
      lastReadingTime = reading.t;
      lastReading = value;
    }
  }

  points = counts.map((count, i) => count > 0 ? Math.round(sums[i] / count * 10) / 10 : null);
  return { points, lastReading, lastReadingTime };
}

/* Every reading at its own time, for sensors whose individual values matter more than the trend */
function buildRawReadingSeries(report, readings) {
  var points = [];
  var lastReading = null;
  var lastReadingTime = null;

  if (report.from == null) return { points, lastReading, lastReadingTime };

  for (let i = 0; i < readings.length; i++) {
    const reading = readings[i];
    const value = Number(reading.v);
    if (isNaN(value) || reading.t < report.from || reading.t > report.to) continue;
    points.push({ x: reading.t, y: value });
    if (lastReadingTime === null || reading.t > lastReadingTime) {
      lastReadingTime = reading.t;
      lastReading = value;
    }
  }

  points.sort((a, b) => a.x - b.x);
  return { points, lastReading, lastReadingTime };
}

function areaGradient(color) {
  return function (context) {
    const chart = context.chart;
    if (!chart.chartArea) return color + "20";
    const gradient = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
    gradient.addColorStop(0, color + "40");
    gradient.addColorStop(1, color + "05");
    return gradient;
  };
}

/* the tooltip needs the timestamp of every bucket, so the options travel with the series */
function buildChartOptions(times) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 600 },
    interaction: { intersect: false, mode: "nearest" },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleColor: "#f1f5f9",
        bodyColor: "#94a3b8",
        borderColor: "#334155",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        displayColors: false,
        callbacks: {
          title: function (items) {
            const time = times[items[0].dataIndex];
            return time == null ? "" : fmtDateTime(time);
          },
          label: function (context) {
            return context.parsed.y === 1 ? "✓ Conectado" : "✗ Desconectado";
          }
        }
      }
    },
    scales: {
      x: {
        type: "category",
        ticks: { color: "#e2e8f0", maxTicksLimit: 12, font: { size: 11 } },
        grid: { color: "#1e293b", drawTicks: false }
      },
      y: {
        min: -0.05,
        max: 1.05,
        ticks: {
          color: "#e2e8f0",
          font: { size: 11 },
          stepSize: 1,
          callback: function (value) {
            return value === 1 ? "Conectado" : value === 0 ? "Desconectado" : "";
          }
        },
        grid: { color: "#1e293b" }
      }
    }
  };
}

function fmtReading(value, unit) {
  return unit ? value + " " + unit : String(value);
}

function buildReadingOptions(times, unit) {
  const options = buildChartOptions(times);
  options.plugins.tooltip.callbacks.label = function (context) {
    return fmtReading(context.parsed.y, unit);
  };
  options.scales.y = {
    grace: "10%",
    ticks: {
      color: "#e2e8f0",
      font: { size: 11 },
      maxTicksLimit: 6,
      callback: function (value) {
        return fmtReading(value, unit);
      }
    },
    grid: { color: "#1e293b" }
  };
  return options;
}

/* raw readings sit on a time axis instead of the bucket labels shared by the other charts */
function buildRawReadingOptions(from, to, unit) {
  const options = buildReadingOptions([], unit);
  const withDay = (to - from) > DAY_MS;
  options.plugins.tooltip.callbacks.title = function (items) {
    return fmtDateTime(items[0].parsed.x);
  };
  options.scales.x = {
    type: "linear",
    min: from,
    max: to,
    ticks: {
      color: "#e2e8f0",
      maxTicksLimit: 12,
      font: { size: 11 },
      callback: function (value) {
        return fmtLabel(value, withDay);
      }
    },
    grid: { color: "#1e293b", drawTicks: false }
  };
  return options;
}

function BusDetails(props) {
  const { device, sessionId } = props;
  const [range, setRange] = useState(function () {
    const to = floorToBucket(Date.now());
    return { from: to - DEFAULT_WINDOW_MS, to: to, live: true };
  });
  const [fromInput, setFromInput] = useState(() => toInputValue(floorToBucket(Date.now()) - DEFAULT_WINDOW_MS));
  const [toInput, setToInput] = useState(() => toInputValue(floorToBucket(Date.now())));
  const [rangeError, setRangeError] = useState(null);
  const [report, setReport] = useState({ from: null, to: null, events: [], temperature: [], acceleration: [] });
  const [loading, setLoading] = useState(true);
  const deviceId = device._id;
  const sensorId = device.temperatureSensorId;
  const accelerometerId = device.accelerometerDeviceId;
  const isLive = range.live;

  const loadEvents = useCallback(() => {
    /* while live the window keeps rolling with the clock, otherwise it is the picked one */
    var to = range.to;
    var from = range.from;
    if (range.live) {
      to = floorToBucket(Date.now());
      from = to - (range.to - range.from);
    }
    const temperatureRequest = sensorId
      ? api.getEvents(sessionId, from, to, sensorId)
      : Promise.resolve(null);
    const accelerometerRequest = accelerometerId
      ? api.getEvents(sessionId, from, to, accelerometerId)
      : Promise.resolve(null);
    const eventsOf = response => {
      const data = (response && response.data) || {};
      return data.events || data.data || [];
    };
    Promise.all([api.getEvents(sessionId, from, to, deviceId), temperatureRequest, accelerometerRequest]).then(function (responses) {
      setReport({
        from: from,
        to: to,
        events: eventsOf(responses[0]),
        temperature: eventsOf(responses[1]).filter(event => event.event === TEMPERATURE_EVENT),
        acceleration: eventsOf(responses[2]).filter(event => event.event === ACCELEROMETER_EVENT)
      });
      setLoading(false);
    });
  }, [sessionId, deviceId, sensorId, accelerometerId, range]);

  useEffect(() => {
    setLoading(true);
    loadEvents();
    /* a picked range is already closed, only the rolling window keeps refreshing */
    if (!isLive) return undefined;
    const interval = setInterval(loadEvents, EVENTS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadEvents, isLive]);

  /* keep the pickers showing the window actually charted while it rolls */
  useEffect(() => {
    if (isLive && report.from != null) {
      setFromInput(toInputValue(report.from));
      setToInput(toInputValue(report.to));
    }
  }, [isLive, report.from, report.to]);

  const applyRange = () => {
    const from = fromInputValue(fromInput);
    const to = fromInputValue(toInput);
    if (from === null || to === null) {
      setRangeError("Selecciona una fecha de inicio y una de término");
      return;
    }
    if (to <= from) {
      setRangeError("La fecha de término debe ser posterior a la de inicio");
      return;
    }
    if (to - from > MAX_RANGE_MS) {
      setRangeError("El rango máximo es de 7 días");
      return;
    }
    setRangeError(null);
    setRange({ from: floorToBucket(from), to: floorToBucket(to), live: false });
  };

  const resetRange = () => {
    const to = floorToBucket(Date.now());
    setRangeError(null);
    setRange({ from: to - DEFAULT_WINDOW_MS, to: to, live: true });
  };

  const series = useMemo(() => buildSeries(report), [report]);

  const chartData = useMemo(() => ({
    labels: series.labels,
    datasets: [{
      data: series.points,
      borderColor: LINE_COLOR,
      backgroundColor: areaGradient(LINE_COLOR),
      borderWidth: 1.5,
      fill: "origin",
      pointRadius: 0,
      pointHitRadius: 8,
      tension: 0,
      stepped: "before"
    }]
  }), [series]);

  const chartOptions = useMemo(() => buildChartOptions(series.times), [series.times]);

  const temperatureSeries = useMemo(() => buildReadingSeries(report, report.temperature), [report]);

  const temperatureData = useMemo(() => ({
    labels: series.labels,
    datasets: [{
      data: temperatureSeries.points,
      borderColor: TEMP_COLOR,
      backgroundColor: areaGradient(TEMP_COLOR),
      borderWidth: 1.5,
      fill: "start",
      pointRadius: 0,
      pointHoverRadius: 4,
      pointBackgroundColor: TEMP_COLOR,
      pointBorderColor: TEMP_COLOR,
      pointHitRadius: 8,
      tension: 0.3,
      /* the sensor reports less often than one bucket, so the line has to bridge the empty buckets */
      spanGaps: true
    }]
  }), [series.labels, temperatureSeries]);

  const temperatureOptions = useMemo(() => buildReadingOptions(series.times, "°C"), [series.times]);

  const accelerationSeries = useMemo(() => buildRawReadingSeries(report, report.acceleration), [report]);

  const accelerationData = useMemo(() => ({
    datasets: [{
      data: accelerationSeries.points,
      showLine: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      pointBackgroundColor: ACCEL_COLOR,
      pointBorderColor: ACCEL_COLOR,
      pointHitRadius: 8
    }]
  }), [accelerationSeries]);

  const accelerationOptions = useMemo(
    () => buildRawReadingOptions(report.from, report.to, "g"),
    [report.from, report.to]
  );

  return (
    <div className="buses-card">
      <div className="buses-card-header">
        <span className="device-name">{device.name} - {device.patente}</span>
        <span className={device.connected ? "buses-status-badge" : "buses-status-badge offline"}>
          <span className={device.connected ? "buses-status-dot" : "buses-status-dot offline"}></span>
          {device.connected ? "Conectado" : "Desconectado"}
        </span>
      </div>

      <div className="buses-card-info">
        <div className="buses-info-item">
          <div className="label">ID Dispositivo</div>
          <div className="value mono">{device._id}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Línea / Recorrido</div>
          <div className="value">{device.linea}/{device.recorrido}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Activo desde</div>
          <div className="value">{series.firstReport ? fmtDateTime(series.firstReport) : "—"}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Último reporte</div>
          <div className="value">{series.lastReport ? fmtDateTime(series.lastReport) : "—"}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Sensor de temperatura</div>
          <div className="value mono">{sensorId || "—"}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Última temperatura</div>
          <div className="value">
            {temperatureSeries.lastReading !== null
              ? temperatureSeries.lastReading + " °C (" + fmtDateTime(temperatureSeries.lastReadingTime) + ")"
              : "—"}
          </div>
        </div>
        <div className="buses-info-item">
          <div className="label">Acelerómetro</div>
          <div className="value mono">{accelerometerId || "—"}</div>
        </div>
        <div className="buses-info-item">
          <div className="label">Última aceleración</div>
          <div className="value">
            {accelerationSeries.lastReading !== null
              ? accelerationSeries.lastReading + " g (" + fmtDateTime(accelerationSeries.lastReadingTime) + ")"
              : "—"}
          </div>
        </div>
      </div>

      <div className="buses-chart-title">Conectividad</div>

      {loading ? (
        <div className="buses-chart-message">Cargando eventos...</div>
      ) : series.firstReport === null ? (
        <div className="buses-chart-message">Sin reportes en el período seleccionado</div>
      ) : (
        <div className="buses-chart-container">
          <Line data={chartData} options={chartOptions} plugins={CHART_PLUGINS} />
        </div>
      )}

      <div className="buses-chart-title">Temperatura</div>
      {!sensorId ? (
        <div className="buses-chart-message">Sin sensor de temperatura asignado</div>
      ) : loading ? (
        <div className="buses-chart-message">Cargando temperatura...</div>
      ) : temperatureSeries.lastReading === null ? (
        <div className="buses-chart-message">Sin lecturas de temperatura en el período seleccionado</div>
      ) : (
        <div className="buses-chart-container">
          <Line data={temperatureData} options={temperatureOptions} plugins={CHART_PLUGINS} />
        </div>
      )}

      <div className="buses-chart-title">Acelerómetro</div>
      {!accelerometerId ? (
        <div className="buses-chart-message">Sin acelerómetro asignado</div>
      ) : loading ? (
        <div className="buses-chart-message">Cargando acelerómetro...</div>
      ) : accelerationSeries.lastReading === null ? (
        <div className="buses-chart-message">Sin lecturas del acelerómetro en el período seleccionado</div>
      ) : (
        <div className="buses-chart-container">
          <Line data={accelerationData} options={accelerationOptions} plugins={CHART_PLUGINS} />
        </div>
      )}

      <div className="buses-range-controls">
        <div className="buses-range-field">
          <label htmlFor="buses-range-from">Desde</label>
          <input
            id="buses-range-from"
            type="datetime-local"
            value={fromInput}
            max={toInput}
            onChange={event => setFromInput(event.target.value)}
          />
        </div>
        <div className="buses-range-field">
          <label htmlFor="buses-range-to">Hasta</label>
          <input
            id="buses-range-to"
            type="datetime-local"
            value={toInput}
            min={fromInput}
            onChange={event => setToInput(event.target.value)}
          />
        </div>
        <button className="buses-range-btn" type="button" onClick={applyRange}>Aplicar</button>
        <button className="buses-range-btn ghost" type="button" onClick={resetRange}>Últimas 24 h</button>
        {rangeError
          ? <span className="buses-range-error">{rangeError}</span>
          : <span className="buses-range-hint">
            Máximo 7 días{isLive ? " · actualizando cada 30 s" : ""}
          </span>}
      </div>
    </div>
  );
}

/* The page reloads the device list every 3 s, only redraw when something shown here moved */
export default React.memo(BusDetails, function (prev, next) {
  return prev.device._id === next.device._id &&
    prev.device.name === next.device.name &&
    prev.device.deviceId === next.device.deviceId &&
    prev.device.connected === next.device.connected &&
    prev.device.temperatureSensorId === next.device.temperatureSensorId &&
    prev.device.accelerometerDeviceId === next.device.accelerometerDeviceId &&
    prev.groupName === next.groupName &&
    prev.sessionId === next.sessionId;
});
